import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, todayISO, formatMoney } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import {
  BookOpen, Plus, Trash2, Save, X, Search, Filter, AlertTriangle,
  CheckCircle2, HelpCircle, ArrowRight, Pencil,
} from "lucide-react";

const STATUS_UI = {
  matched:  { cls: "bg-[#DCFCE7] text-[#15803D]", icon: CheckCircle2,  label: "Matched" },
  mismatch: { cls: "bg-[#FEE2E2] text-[#B91C1C]", icon: AlertTriangle, label: "Mismatch" },
  unmatched:{ cls: "bg-[#FEF3C7] text-[#B45309]", icon: HelpCircle,    label: "No txn found" },
};

function EntryModal({ initial, parties, brands, sizes, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || {
      date: todayISO(),
      kind: "sale",
      party_name: "",
      party_id: "",
      challan_no: "",
      items: [{ brand: brands[0] || "", size: sizes[0] || "", filled_qty: "", mt_qty: "" }],
      notes: "",
    }
  );
  const [busy, setBusy] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);

  useEffect(() => {
    const q = (form.party_name || "").trim().toLowerCase();
    if (!q) { setPartyOptions([]); return; }
    setPartyOptions(
      parties
        .filter((p) => (p.name || "").toLowerCase().includes(q) && p.name.toLowerCase() !== q)
        .slice(0, 6)
    );
  }, [form.party_name, parties]);

  const setItem = (i, patch) => {
    setForm({ ...form, items: form.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { brand: brands[0] || "", size: sizes[0] || "", filled_qty: "", mt_qty: "" }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!form.date || !form.party_name.trim()) { toast.error("Date and party required"); return; }
    if (!form.items.length || !form.items.some((it) => Number(it.filled_qty) > 0 || Number(it.mt_qty) > 0)) {
      toast.error("Add at least one item with a quantity"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((it) => ({
          brand: it.brand,
          size: it.size,
          filled_qty: Number(it.filled_qty) || 0,
          mt_qty: Number(it.mt_qty) || 0,
        })),
      };
      if (initial?.id) {
        await api.patch(`/register-entries/${initial.id}`, payload);
        toast.success("Register entry updated");
      } else {
        await api.post("/register-entries", payload);
        toast.success("Register entry saved");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-sm sm:rounded-sm w-full sm:max-w-2xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="register-entry-modal">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="small-label text-gray-500">Physical Register</div>
            <div className="font-heading font-black text-xl">{initial?.id ? "Edit entry" : "New register entry"}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="small-label block mb-1.5">Date</label>
              <input type="date" className="input-lg" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="re-date" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Type</label>
              <select className="input-lg" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="re-kind">
                <option value="sale">Sale</option>
                <option value="purchase">Purchase</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="small-label block mb-1.5">Challan No.</label>
              <input className="input-lg" placeholder="as written in register" value={form.challan_no}
                onChange={(e) => setForm({ ...form, challan_no: e.target.value })} data-testid="re-challan" />
            </div>
          </div>
          <div className="relative">
            <label className="small-label block mb-1.5">Party name</label>
            <input className="input-lg" placeholder="Type to search parties…"
              value={form.party_name}
              onChange={(e) => setForm({ ...form, party_name: e.target.value, party_id: "" })}
              data-testid="re-party" />
            {partyOptions.length > 0 && (
              <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-sm shadow z-20 max-h-40 overflow-auto">
                {partyOptions.map((p) => (
                  <button key={p.id} type="button"
                    onClick={() => setForm({ ...form, party_name: p.name, party_id: p.id })}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 flex items-center gap-2"
                    data-testid={`re-party-opt-${p.id}`}>
                    <span className="flex-1">{p.name}</span>
                    <span className="text-[10px] text-gray-500 uppercase">{p.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="small-label">Items</div>
              <button onClick={addItem} className="text-xs text-black font-semibold flex items-center gap-1" data-testid="re-add-item">
                <Plus size={12} /> Add row
              </button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`re-item-${i}`}>
                  <select className="input-lg col-span-3" value={it.brand} onChange={(e) => setItem(i, { brand: e.target.value })}>
                    {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <select className="input-lg col-span-3" value={it.size} onChange={(e) => setItem(i, { size: e.target.value })}>
                    {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <input type="number" step="1" className="input-lg col-span-2 text-right data-num"
                    placeholder="Filled" value={it.filled_qty}
                    onChange={(e) => setItem(i, { filled_qty: e.target.value })} data-testid={`re-item-filled-${i}`} />
                  <input type="number" step="1" className="input-lg col-span-2 text-right data-num"
                    placeholder="MT" value={it.mt_qty}
                    onChange={(e) => setItem(i, { mt_qty: e.target.value })} data-testid={`re-item-mt-${i}`} />
                  <button onClick={() => removeItem(i)} disabled={form.items.length === 1}
                    className="col-span-2 text-gray-400 hover:text-[#B91C1C] p-2 disabled:opacity-30" title="Remove"
                    data-testid={`re-item-remove-${i}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="small-label block mb-1.5">Notes</label>
            <textarea className="input-lg min-h-[60px] py-2" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything worth noting…" data-testid="re-notes" />
          </div>
        </div>
        <div className="p-5 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2" data-testid="re-save">
            <Save size={16} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const cfg = STATUS_UI[status] || STATUS_UI.unmatched;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-sm ${cfg.cls}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
};

export default function RegisterEntry() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [parties, setParties] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sizes, setSizes] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: statusFilter, kind: kindFilter };
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      if (search) params.search = search;
      const { data } = await api.get("/register-entries", { params });
      setRows(data.rows || []);
      setSummary(data.summary || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const loadMeta = async () => {
    try {
      const [{ data: p }, { data: b }, { data: s }] = await Promise.all([
        api.get("/parties"),
        api.get("/brands"),
        api.get("/sizes"),
      ]);
      setParties(p || []);
      setBrands((b || []).map((x) => x.name).filter(Boolean));
      setSizes((s || []).map((x) => x.name).filter(Boolean));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { load(); }, [statusFilter, kindFilter]);
  // Debounced search / date reload
  useEffect(() => { const t = setTimeout(load, 400); return () => clearTimeout(t); }, [search, from, to]);

  const remove = async (r) => {
    if (!window.confirm(`Delete register entry for ${r.party_name}?`)) return;
    try {
      await api.delete(`/register-entries/${r.id}`);
      toast.success("Entry deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="register-entry-page">
      <div className="flex items-center gap-3 flex-wrap">
        <BookOpen size={20} className="text-black" />
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">Physical Register</h2>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Tally paper register against system sales / purchases
        </span>
        <button onClick={() => { setEditing(null); setShowModal(true); }}
          className="ml-auto btn-primary h-11 px-5 flex items-center gap-2" data-testid="re-new-btn">
          <Plus size={16} /> New entry
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Total register entries</div>
          <div className="data-num text-3xl mt-1" data-testid="re-total">{summary.total || 0}</div>
        </div>
        <div className="card-flat p-5 bg-[#DCFCE7] border-[#86EFAC]">
          <div className="small-label text-[#15803D] flex items-center gap-1.5"><CheckCircle2 size={12} /> Matched</div>
          <div className="data-num text-3xl mt-1 text-[#15803D]" data-testid="re-matched">{summary.matched || 0}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><AlertTriangle size={12} /> Qty mismatch</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="re-mismatch">{summary.mismatch || 0}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309] flex items-center gap-1.5"><HelpCircle size={12} /> No txn found</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="re-unmatched">{summary.unmatched || 0}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
        <Filter size={14} className="text-gray-500" />
        {["all", "matched", "mismatch", "unmatched"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`h-9 px-3 rounded-sm text-xs font-semibold border ${statusFilter === s ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
            data-testid={`re-filter-${s}`}>
            {s === "all" ? "All" : STATUS_UI[s]?.label || s}
          </button>
        ))}
        <select className="input-lg h-9 w-auto" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
          data-testid="re-kind-filter">
          <option value="all">All types</option>
          <option value="sale">Sale</option>
          <option value="purchase">Purchase</option>
        </select>
        <input type="date" className="input-lg h-9 w-auto" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="re-from" />
        <span className="text-xs text-gray-400">to</span>
        <input type="date" className="input-lg h-9 w-auto" value={to} onChange={(e) => setTo(e.target.value)} data-testid="re-to" />
        <div className="relative ml-auto">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input-lg h-9 pl-8 w-56" placeholder="Search party / challan…"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="re-search" />
        </div>
      </div>

      {/* Table */}
      <div className="card-flat overflow-auto">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Date</th>
              <th className="text-left">Type</th>
              <th className="text-left">Challan</th>
              <th className="text-left">Party</th>
              <th className="text-left">Items</th>
              <th className="text-left">Status / Mismatch</th>
              <th className="text-left">Entered by</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="8" className="py-8 text-center text-sm text-gray-400">
                <BookOpen size={26} className="inline-block text-gray-300 mb-1" />
                <div>No register entries yet. Click &quot;New entry&quot; to add one.</div>
              </td></tr>
            )}
            {rows.map((r) => {
              const rowBg = r.status === "mismatch" ? "bg-[#FEE2E2]/40" : r.status === "unmatched" ? "bg-[#FEF3C7]/40" : "";
              return (
                <tr key={r.id} className={rowBg} data-testid={`re-row-${r.id}`}>
                  <td className="data-num text-xs">{r.date}</td>
                  <td className="capitalize text-xs">{r.kind}</td>
                  <td className="data-num text-sm font-semibold">{r.challan_no || <span className="text-gray-400 italic">no #</span>}</td>
                  <td>
                    {r.party_id
                      ? <Link to={`/parties/${r.party_id}`} className="hover:underline text-sm">{r.party_name}</Link>
                      : <span className="text-sm">{r.party_name}</span>}
                  </td>
                  <td className="text-xs">
                    {(r.items || []).map((it, i) => (
                      <div key={i}>
                        <span className="font-semibold">{it.brand} · {it.size}</span>
                        <span className="text-gray-500"> — F {it.filled_qty}{it.mt_qty ? `, MT ${it.mt_qty}` : ""}</span>
                      </div>
                    ))}
                  </td>
                  <td>
                    <StatusPill status={r.status} />
                    {r.status === "mismatch" && (r.item_diffs || []).length > 0 && (
                      <div className="mt-1 text-[11px] text-[#B91C1C] space-y-0.5">
                        {r.item_diffs.slice(0, 3).map((d, i) => (
                          <div key={i}>
                            <b>{d.brand} {d.size}</b>: reg {d.register_filled}F/{d.register_mt}MT vs sys {d.txn_filled}F/{d.txn_mt}MT
                          </div>
                        ))}
                        {r.item_diffs.length > 3 && <div>+{r.item_diffs.length - 3} more…</div>}
                      </div>
                    )}
                    {r.txn && (
                      <div className="mt-1 text-[10px] text-gray-500 flex items-center gap-1">
                        <ArrowRight size={9} /> matched to challan {r.txn.challan_no || "—"}
                      </div>
                    )}
                  </td>
                  <td className="text-xs text-gray-500">{r.entered_by_name || r.entered_by_email || "—"}</td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => { setEditing(r); setShowModal(true); }}
                        className="text-gray-500 hover:text-black p-1" title="Edit"
                        data-testid={`re-edit-${r.id}`}><Pencil size={13} /></button>
                      <button onClick={() => remove(r)}
                        className="text-gray-400 hover:text-[#B91C1C] p-1" title="Delete"
                        data-testid={`re-del-${r.id}`}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <EntryModal
          initial={editing}
          parties={parties}
          brands={brands}
          sizes={sizes}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={() => { setShowModal(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
